import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuthOptional } from '../lib/auth';
import { useApi } from './kit';

// Back from a platform's consent screen (?org=… on the callback's redirect): the connect may have started in another
// workspace than the one open now (a second tab, a link opened later, a connection started for another workspace).

/** The workspace the connection was made in, when it is not the open one; else null. */
export function useOtherWorkspace(): string | null {
  const auth = useAuthOptional();
  const [params] = useSearchParams();
  const org = params.get('org');
  const me = auth?.me;
  return org && me && org !== (me.org.sandbox_of ?? me.org.id) ? org : null;
}

/** Switches to that workspace and comes back to the same page to finish, or says where it went if this person is not in it. */
export function OtherWorkspace({ orgId }: { orgId: string }) {
  const auth = useAuthOptional();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const orgs = useApi<{ data: Array<{ id: string; name: string }> }>('/portal/my-orgs');
  const target = orgs.data?.data.find((o) => o.id === orgId);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    if (!target) return;
    void (async () => {
      try {
        await api('/portal/switch-org', { method: 'POST', body: { org_id: orgId } });
        await auth?.supabase?.auth.refreshSession(); await auth?.refresh();
        const next = new URLSearchParams(params); next.delete('org');
        nav(`${pathname}?${next}`, { replace: true });
      } catch (e) { setFailed((e as Error).message); }
    })();
  }, [target?.id]);
  if (orgs.isLoading || (target && !failed)) return <p className="muted" role="status">{target ? `Switching to ${target.name} to finish connecting…` : 'Finishing the connection…'}</p>;
  if (failed) return <p className="error" role="alert">The account was connected in {target?.name ?? 'another workspace'}, but switching to it did not work ({failed}). Open that workspace from the workspace menu to finish there.</p>;
  return <p className="small" role="status">The account was connected in another workspace, one you are not a member of, so there is nothing to finish here. <Link to={pathname}>Back</Link></p>;
}
